import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Paperclip, Send, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { TID } from "@/constants/testIds";

export default function Consultation() {
    const { id } = useParams();
    const { user } = useAuth();
    const [meta, setMeta] = useState(null);
    const [messages, setMessages] = useState([]);
    const [text, setText] = useState("");
    const [sending, setSending] = useState(false);
    const [pending, setPending] = useState(null); // pending image preview
    const listRef = useRef(null);
    const fileRef = useRef(null);

    const load = useCallback(async () => {
        try {
            const [m, msgs] = await Promise.all([
                api.get(`/consultations/${id}`),
                api.get(`/chat/${id}`),
            ]);
            setMeta(m.data);
            setMessages(msgs.data);
        } catch (e) {
            console.error(e);
        }
    }, [id]);

    useEffect(() => {
        load();
        // Poll every 4 seconds
        const iv = setInterval(load, 4000);
        return () => clearInterval(iv);
    }, [load]);

    useEffect(() => {
        if (listRef.current) {
            listRef.current.scrollTop = listRef.current.scrollHeight;
        }
    }, [messages, pending]);

    function onPickFile(e) {
        const file = e.target.files?.[0];
        if (!file) return;
        if (file.size > 5 * 1024 * 1024) {
            toast.error("Image too large. Please pick under 5 MB.");
            return;
        }
        const reader = new FileReader();
        reader.onload = () => setPending(reader.result);
        reader.readAsDataURL(file);
    }

    async function send() {
        const t = text.trim();
        if (!t && !pending) return;
        setSending(true);
        try {
            const body = { text: t };
            if (pending) body.image_base64 = pending;
            const { data } = await api.post(`/chat/${id}`, body);
            setMessages((prev) => [...prev, data]);
            setText("");
            setPending(null);
            if (fileRef.current) fileRef.current.value = "";
        } catch (e) {
            const detail = e?.response?.data?.detail;
            toast.error(typeof detail === "string" ? detail : "Send failed.");
        } finally {
            setSending(false);
        }
    }

    if (!meta) {
        return (
            <div className="p-8 text-center text-sm text-veda-muted">Loading...</div>
        );
    }

    const otherName =
        user?.role === "customer" ? meta.doctor_name : meta.customer_name;

    return (
        <div className="mx-auto flex h-[calc(100vh-4rem)] max-w-3xl flex-col border-x border-veda-border">
            <div className="border-b border-veda-border bg-veda-surface px-5 py-3 md:px-8">
                <Link
                    to={user?.role === "doctor" ? "/doctor/chats" : "/orders"}
                    className="inline-flex items-center gap-1 text-xs font-medium text-veda-muted"
                >
                    <ArrowLeft size={14} /> Back
                </Link>
                <div className="mt-2 flex items-center gap-3">
                    {meta.doctor_avatar && (
                        <img
                            src={meta.doctor_avatar}
                            alt=""
                            className="h-10 w-10 object-cover"
                        />
                    )}
                    <div>
                        <p className="font-display text-lg text-veda-onSurface md:text-xl">
                            {otherName}
                        </p>
                        <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-veda-muted">
                            {meta.mode} · {meta.slot}
                        </p>
                    </div>
                </div>
            </div>

            <div ref={listRef} className="flex-1 overflow-y-auto bg-veda-surface2 px-4 py-4 md:px-8 md:py-6">
                {messages.length === 0 && (
                    <p className="mt-8 text-center text-xs text-veda-muted">
                        Say hello to get the consultation started.
                    </p>
                )}
                <div className="flex flex-col gap-2">
                    {messages.map((m) => {
                        const mine = m.sender_id === user?.user_id;
                        return (
                            <div
                                key={m.message_id}
                                className={`flex ${
                                    mine ? "justify-end" : "justify-start"
                                }`}
                            >
                                <div
                                    className={`max-w-[80%] border px-3 py-2 text-sm ${
                                        mine
                                            ? "border-veda-brand bg-veda-brand text-white"
                                            : "border-veda-border bg-veda-surface text-veda-onSurface"
                                    }`}
                                >
                                    {m.image_base64 && (
                                        <img
                                            src={m.image_base64}
                                            alt=""
                                            className="mb-2 max-h-64 w-full object-contain"
                                        />
                                    )}
                                    {m.text && <p>{m.text}</p>}
                                    <p
                                        className={`mt-1 text-[10px] uppercase tracking-[0.16em] ${
                                            mine ? "text-white/70" : "text-veda-muted"
                                        }`}
                                    >
                                        {m.sender_name} · {m.sender_role}
                                    </p>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>

            {pending && (
                <div className="border-t border-veda-border bg-veda-surface p-3">
                    <div className="flex items-center gap-2">
                        <img
                            src={pending}
                            alt="preview"
                            className="h-14 w-14 object-cover"
                        />
                        <p className="flex-1 text-xs text-veda-muted">
                            Image attached
                        </p>
                        <button
                            onClick={() => {
                                setPending(null);
                                if (fileRef.current) fileRef.current.value = "";
                            }}
                            className="text-xs font-semibold uppercase tracking-[0.14em] text-veda-error"
                        >
                            Remove
                        </button>
                    </div>
                </div>
            )}

            <div className="border-t border-veda-border bg-veda-surface px-3 py-2">
                <div className="flex items-center gap-2">
                    <button
                        data-testid={TID.chatAttach}
                        onClick={() => fileRef.current?.click()}
                        className="grid h-9 w-9 place-items-center border border-veda-border"
                        aria-label="Attach"
                    >
                        <Paperclip size={15} />
                    </button>
                    <input
                        ref={fileRef}
                        type="file"
                        accept="image/*"
                        onChange={onPickFile}
                        className="hidden"
                    />
                    <input
                        data-testid={TID.chatInput}
                        value={text}
                        onChange={(e) => setText(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === "Enter" && !e.shiftKey) {
                                e.preventDefault();
                                send();
                            }
                        }}
                        placeholder="Message..."
                        className="flex-1 border border-veda-border bg-veda-surface px-3 py-2 text-sm"
                    />
                    <button
                        data-testid={TID.chatSend}
                        onClick={send}
                        disabled={sending || (!text.trim() && !pending)}
                        className="inline-flex h-9 w-9 items-center justify-center bg-veda-brand text-white disabled:opacity-60"
                        aria-label="Send"
                    >
                        {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
                    </button>
                </div>
            </div>
        </div>
    );
}
